import {Axios} from "axios";
import {CatalogValueDto} from "../catalog/CatalogValueDto";
import {UserDto} from "../user/User";
import {PageableDto} from "../pagination/PagingFilterDto";
import {SortableDto} from "../pagination/SortingFilterDto";
import {PageableResultListDto} from "../pagination/PageableResultListDto";

export interface SubmissionDto {
    id: number;
    submissionNumber: string;
    referenceNumber?: string;
    documentMetadata: DocumentMetadataDto;
    currentSubmissionState: SubmissionStateDto;

    submittedAt?: string;
    submittedBy?: UserDto;

    digitalActionAuthorization?: DigitalActionAuthorizationDto;
    paymentRequired?: boolean;
}

export interface DocumentMetadataDto {
    type?: CatalogValueDto;
    version?: CatalogValueDto;
    revision?: string;
    agent?: string;
}

export interface SubmissionStateDto {
    id: number;
    type: CatalogValueDto;
    note?: string;

    triggeredAt?: string;
    triggeredBy?: UserDto;
}

export interface DigitalActionAuthorizationDto {
    digitalActionId?: string;
    authorizationTime?: string;
    authorizationResult?: DigitalActionAuthorizationResult;
    errorCode?: string;
    errorMessage?: string;
}

export enum SubmissionStateType {
    ADDITIONAL_EXTRACTION_INFORMATION_OBTAINED = "ADDITIONAL_EXTRACTION_INFORMATION_OBTAINED",
    AUTHORIZATION_INITIALIZATION_FAILED = "AUTHORIZATION_INITIALIZATION_FAILED",
    AUTHORIZATION_IN_PROGRESS = "AUTHORIZATION_IN_PROGRESS",
    AUTHORIZED = "AUTHORIZED",
    CERTIFICATION_GENERATED = "CERTIFICATION_GENERATED",
    CERTIFICATION_SIGNED = "CERTIFICATION_SIGNED",
    DELIVERY_TO_EXTERNAL_SYSTEM_INITIALIZED = "DELIVERY_TO_EXTERNAL_SYSTEM_INITIALIZED",
    METADATA_EXTRACTED = "METADATA_EXTRACTED",
    METADATA_EXTRACTION_FAILED = "METADATA_EXTRACTION_FAILED",
    NOT_AUTHORIZED = "NOT_AUTHORIZED",
    OBTAINING_ADDITIONAL_EXTRACTION_INFORMATION = "OBTAINING_ADDITIONAL_EXTRACTION_INFORMATION",
    PAYMENT_INFORMATION_CREATION_FAILED = "PAYMENT_INFORMATION_CREATION_FAILED",
    PAYMENT_INFORMATION_PROCESSED = "PAYMENT_INFORMATION_PROCESSED",
    PROCESSED = "PROCESSED",
    SUPPORTING_DOCUMENTS_CREATED = "SUPPORTING_DOCUMENTS_CREATED",
    SUPPORTING_DOCUMENTS_CREATION_FAILED = "SUPPORTING_DOCUMENTS_CREATION_FAILED",
    SUPPORTING_DOCUMENTS_TIMESTAMPED = "SUPPORTING_DOCUMENTS_TIMESTAMPED",
    UNPROCESSABLE = "UNPROCESSABLE",
    UPLOADED = "UPLOADED",
    VALIDATED = "VALIDATED",
    WAITING_FOR_AUTHORIZATION = "WAITING_FOR_AUTHORIZATION",
    WAITING_FOR_DELIVERY_INITIALIZATION = "WAITING_FOR_DELIVERY_INITIALIZATION",
    WAITING_FOR_METADATA_EXTRACTION = "WAITING_FOR_METADATA_EXTRACTION",
    WAITING_FOR_VALIDATION = "WAITING_FOR_VALIDATION"
}

export enum SubmissionDeliveryStateType {
    INITIALIZING_DELIVERY = "INITIALIZING_DELIVERY",
    DELIVERY_FAILED = "DELIVERY_FAILED",
    DELIVERING = "DELIVERING",
    DELIVERED = "DELIVERED",
}

export enum DigitalActionAuthorizationResult {
    SUCCESS = "SUCCESS",
    ERROR = "ERROR",
}

export enum DocumentType {
    NOT_DETECTED_YET = "NOT_DETECTED_YET",
    UNKNOWN = "UNKNOWN",
}

export interface SubmissionFilterDto extends PageableDto, SortableDto {
    submittedById?: number;
    submissionNumberLike?: string;
    hasNotSubmissionAttachmentTypes?: SubmissionAttachmentType[];
    referenceNumberLike?: string;
    stateUpdatedAtFrom?: Date;
    stateUpdatedAtTo?: Date;
    submissionStateTypeCodes?: SubmissionStateType[];
    submittedAtFrom?: Date;
    submittedAtTo?: Date;
}

export enum SubmissionAttachmentType {
    ORIGINAL= "ORIGINAL",
    PRINTABLE_DOCUMENT = "PRINTABLE_DOCUMENT",
    CERTIFICATION = "CERTIFICATION",
    COVER_LETTER = "COVER_LETTER",
    PAYMENT_INFORMATION = "PAYMENT_INFORMATION",
    OTHER = "OTHER",
}

export enum TimestampAttachmentStatus {
    NO_TIMESTAMP = "NO_TIMESTAMP",
    WAITING_FOR_TIMESTAMP = "WAITING_FOR_TIMESTAMP",
    TIMESTAMPED = "TIMESTAMPED",
    TIMESTAMP_EXPIRED = "TIMESTAMP_EXPIRED",
}

export interface SubmissionAttachmentDto {
    id: number;
    submissionId: number;
    attachmentType: SubmissionAttachmentType;
    fileName: string;
    fileExtension: string;
    size: number;
    timestampStatus?: TimestampAttachmentStatus;
}

export interface SubmissionAttachmentFilterDto {
    includeNotStamped?: boolean;
    attachmentTypes?: SubmissionAttachmentType[];
}

export enum EmailDeliveryRecipientType {
    RECIPIENT = "RECIPIENT",
    SENDER = "SENDER",
}

export enum SubmissionDeliveryChannel {
    EMAIL = "EMAIL",
    ISDS = "ISDS",
}

export interface SubmissionDeliveryDto {
    id: number;
    submissionId: number;

    channel: SubmissionDeliveryChannel;
    isdsRecipientDatabox?: string;
    emailRecipient?: string;
    recipientType?: EmailDeliveryRecipientType;
    currentStateType?: SubmissionDeliveryStateType;
    updatedAt?: Date;
}

export interface SubmissionDeliveryAclDto {
    canRedeliverSubmission?: boolean;
}

export interface SubmissionDeliveryResultListDto {
    items: SubmissionDeliveryDto[];
    acl?: SubmissionDeliveryAclDto;
}

export class SubmissionApiClient {

    constructor(private readonly axios: Axios) {
    }

    findById(submissionId: number): Promise<SubmissionDto | undefined> {
        return new Promise((resolve, reject) => {
            this.axios.get<SubmissionDto | undefined>(`/submissions/${submissionId}`)
                .then((response) => resolve(response.data))
                .catch((error) => {
                    if (error?.response?.status === 404) {
                        resolve(undefined);
                    }
                    reject(error);
                });
        });
    }

    findSubmissionDeliveries(submissionId: number): Promise<SubmissionDeliveryResultListDto> {
        return new Promise((resolve, reject) => {
            this.axios.get<SubmissionDeliveryResultListDto>(`/submissions/${submissionId}/deliveries`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    redeliverDelivery(submissionId: number, deliveryId: number): Promise<SubmissionDeliveryDto> {
        return new Promise((resolve, reject) => {
            this.axios.post<SubmissionDeliveryDto>(`/submissions/${submissionId}/deliveries/${deliveryId}:redeliver`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    findByFilter(filter: SubmissionFilterDto): Promise<PageableResultListDto<SubmissionDto>> {
        return new Promise((resolve, reject) => {
            this.axios.post<Promise<PageableResultListDto<SubmissionDto>>>("/submissions:search", filter)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    countByFilter(filter: SubmissionFilterDto): Promise<number> {
        return new Promise((resolve, reject) => {
            this.axios.post<number>("/submissions:count", filter)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    findSubmissionStates(submissionId: number): Promise<SubmissionStateDto[]> {
        return new Promise((resolve, reject) => {
            this.axios.get<SubmissionStateDto[]>(`/submissions/${submissionId}/states`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    findSubmissionAttachments(submissionId: number): Promise<SubmissionAttachmentDto[]> {
        return this.findAttachmentsByFilter(submissionId, {includeNotStamped: true});
    }

    downloadAttachmentDocument(submissionId: number, attachmentId: number): Promise<Blob> {
        return new Promise((resolve, reject) => {
            this.axios.get<Blob>(`/submissions/${submissionId}/attachments/${attachmentId}/document`, {responseType: "blob"})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    async downloadSubmittedDocument(submissionId: number): Promise<Blob> {
        return this.downloadAttachmentByType(submissionId, SubmissionAttachmentType.ORIGINAL);
    }

    async downloadSubmittedPrintableDocument(submissionId: number): Promise<Blob> {
        return this.downloadAttachmentByType(submissionId, SubmissionAttachmentType.PRINTABLE_DOCUMENT);
    }

    async downloadSubmissionCertificationDocument(submissionId: number): Promise<Blob> {
        return this.downloadAttachmentByType(submissionId, SubmissionAttachmentType.CERTIFICATION);
    }

    async downloadSubmissionCoverLetterDocument(submissionId: number): Promise<Blob> {
        return this.downloadAttachmentByType(submissionId, SubmissionAttachmentType.COVER_LETTER);
    }

    async downloadSubmissionPaymentInformationDocument(submissionId: number): Promise<Blob> {
        return this.downloadAttachmentByType(submissionId, SubmissionAttachmentType.PAYMENT_INFORMATION);
    }

    async findFirstAttachmentByType(submissionId: number, attachmentType: SubmissionAttachmentType): Promise<SubmissionAttachmentDto | undefined> {
        const attachments = await this.findAttachmentsByFilter(submissionId, {
            includeNotStamped: false,
            attachmentTypes: [attachmentType],
        });
        return attachments[0];
    }

    findAttachmentsByFilter(submissionId: number, filter: SubmissionAttachmentFilterDto): Promise<SubmissionAttachmentDto[]> {
        return new Promise((resolve, reject) => {
            this.axios.post<SubmissionAttachmentDto[]>(`/submissions/${submissionId}/attachments:search`, filter)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    async downloadSubmissionCertificationDocumentNotStamped(submissionId: number): Promise<Blob> {
        return this.downloadAttachmentByFilter(submissionId, {
            includeNotStamped: true,
            attachmentTypes: [SubmissionAttachmentType.CERTIFICATION],
        });
    }

    private async downloadAttachmentByType(submissionId: number, attachmentType: SubmissionAttachmentType): Promise<Blob> {
        return this.downloadAttachmentByFilter(submissionId, {
            includeNotStamped: false,
            attachmentTypes: [attachmentType],
        });
    }

    private async downloadAttachmentByFilter(submissionId: number, filter: SubmissionAttachmentFilterDto): Promise<Blob> {
        const attachments = await this.findAttachmentsByFilter(submissionId, filter);
        const attachment = attachments[0];

        if (!attachment) {
            throw new Error(`${filter.attachmentTypes?.[0]} document not found for submission ID: ${submissionId}`);
        }

        const response = await this.axios.get<Blob>(
            `/submissions/${submissionId}/attachments/${attachment.id}/document`,
            { responseType: "blob" }
        );

        return response.data;
    }
}
